import MuiLink from '@mui/material/Link';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { resolveSinglePartId, resolveSingleServiceId } from './resolveSingleRecordId';

export interface HierarchyCountBadgeProps {
  nodeId: number;
  count: number;
  kind: 'services' | 'parts';
  /** This node's own (pre-sum) count for `kind` — `count` is a subtree
   *  total (self + descendants; see HierarchyTree.buildTotalCountsById), so
   *  `count === 1` doesn't by itself mean the record is filed at this node —
   *  it could be on a descendant. Only when `ownCount` also equals 1 do we
   *  know it's this node's own record, and only then do we attempt to
   *  resolve and link straight to it. Omit for a row with no descendants
   *  (own === total always), where `count` alone already answers the
   *  question. */
  ownCount?: number;
}

// `parts` deliberately doesn't use `v4.tableSelected` — that's the same
// token the table uses to highlight the current job's own row, and reusing
// it here made an unrelated badge read as if it meant "this is the active
// row." `v4.filterInput` is the same neutral pill background already used
// for the field-name label inside a filter chip — visually distinct from
// both the active-row highlight and the `services` badge's accent colour.
const BADGE_STYLE = {
  services: { label: 'job', bgcolor: 'v4.accent', color: 'common.white' },
  parts: { label: 'part', bgcolor: 'v4.filterInput', color: 'text.secondary' },
} as const;

/** Build the deep-link each badge kind navigates to when it can't (or
 *  doesn't need to) resolve a single record directly. `services` has its
 *  own registered `?locationId=` deep-link on `/services`
 *  (useServiceListData.ts reads it directly). Parts has no such route or
 *  param — `/parts` isn't even a registered path (the list lives at
 *  `/parts/search`), and its scoping mechanism is the advanced-filter
 *  codec, so a "Node ID equals X" filter has to go on as
 *  `af=nodeId|equals|X`, the same shape the Parts search page's own
 *  "Node ID" advanced filter produces. */
const buildListHref = (kind: HierarchyCountBadgeProps['kind'], nodeId: number): string =>
  kind === 'services'
    ? `/services?locationId=${nodeId}`
    : `/parts/search?af=${encodeURIComponent(`nodeId|equals|${nodeId}`)}`;

const pillSx = {
  fontSize: '10px',
  fontWeight: 600,
  px: 0.75,
  py: '1px',
  borderRadius: '10px',
  lineHeight: '16px',
  '&:hover': { opacity: 0.85 },
} as const;

/** A single linked job/part count badge — hidden when the count is zero.
 *  Shared by the tree (HierarchyNodeCounts, both counts together) and the
 *  table (each count in its own column).
 *
 * Always renders as a plain link — clicking is never itself what triggers a
 * lookup. When the count is a node's own single record (`count === 1 &&
 * ownCount === 1`), the id is resolved *eagerly on mount* (reusing the same
 * list endpoints the filtered-list link already hits, just capped to one
 * row) so the link can point straight at the record — the click itself
 * stays instant either way. Before that resolves (or if it resolves to
 * nothing, e.g. the record was deleted since the hierarchy snapshot was
 * fetched), the link falls back to the ordinary filtered-list href, so a
 * click landing in that brief window is never worse than today's default
 * behaviour, just not upgraded yet. */
export const HierarchyCountBadge: React.FC<HierarchyCountBadgeProps> = ({
  nodeId,
  count,
  kind,
  ownCount,
}) => {
  const isExactSingleRecord = count === 1 && (ownCount ?? count) === 1;
  // undefined = not yet resolved; null = resolved, but no record found.
  const [resolvedId, setResolvedId] = useState<number | null | undefined>(undefined);

  useEffect(() => {
    if (!isExactSingleRecord) return undefined;
    let cancelled = false;
    const resolve = kind === 'services' ? resolveSingleServiceId : resolveSinglePartId;
    resolve(nodeId).then((id) => {
      if (!cancelled) setResolvedId(id);
    });
    return () => {
      cancelled = true;
    };
  }, [isExactSingleRecord, kind, nodeId]);

  if (count <= 0) return null;
  const { label, bgcolor, color } = BADGE_STYLE[kind];
  const text = `${count} ${count === 1 ? label : `${label}s`}`;
  const href =
    isExactSingleRecord && resolvedId != null
      ? `/${kind}/${resolvedId}`
      : buildListHref(kind, nodeId);

  return (
    <MuiLink
      component={RouterLink}
      to={href}
      underline="none"
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      sx={{ ...pillSx, bgcolor, color }}
    >
      {text}
    </MuiLink>
  );
};
